"use client";

import React, { useMemo, useCallback } from "react";
import { ResponsiveModal, ModalContainer, MODAL_IDS } from "@west-tennessee-consulting/ui";
import { createEnhancedGenericForm } from "@/components/shared/EnhancedGenericCreateForm";
import type { BaseEntity } from "@/components/shared/UniversalCRMTable/types";
import { ENHANCED_DEALS_CONFIG } from "@/features/deals/components/EnhancedDealsConfig";
import { useCreateDeal } from "@/features/deals/hooks/use-deals-hooks";
import { useFormFeedback } from "@/lib/hooks/forms/use-feedback";
import type { CreateDealModalData } from "@/lib/modals/data/modal-data-types";
import type { Deal } from "@/lib/types/database/deals";

const DEALS_CONFIG = ENHANCED_DEALS_CONFIG;

export function QuickDealCreationModal() {
  return (
    <ModalContainer<CreateDealModalData> modalId={MODAL_IDS.deals.createQuick}>
      {({ isOpen, data, onClose }) => (
        <QuickDealCreationModalContent
          isOpen={isOpen}
          data={data}
          onClose={onClose}
        />
      )}
    </ModalContainer>
  );
}

interface QuickDealCreationModalContentProps {
  isOpen: boolean;
  data?: CreateDealModalData;
  onClose: () => void;
}

function QuickDealCreationModalContent({
  isOpen,
  data,
  onClose,
}: QuickDealCreationModalContentProps) {
  const { mutateAsync: createDeal, isPending } = useCreateDeal();
  const feedback = useFormFeedback({
    successMessage: "Deal created successfully!",
  });

  const DealForm = useMemo(
    () => createEnhancedGenericForm("deals", DEALS_CONFIG),
    []
  );

  const initialValues = useMemo((): Partial<BaseEntity> => {
    const values: Partial<BaseEntity> = {};
    if (data?.companyId) values.company_id = data.companyId;
    return values;
  }, [data?.companyId]);

  const handleSubmit = useCallback(
    async (formData: Record<string, unknown>) => {
      try {
        const created = await createDeal(formData);
        if (created?.id) {
          onClose();
        }
      } catch (error) {
        feedback.showError(
          error instanceof Error ? error.message : "Failed to create deal"
        );
        throw error;
      }
    },
    [createDeal, onClose, feedback]
  );

  return (
    <ResponsiveModal
      globalModalId={MODAL_IDS.deals.createQuick}
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={data?.companyName ? `Create Deal for ${data.companyName}` : "Create Deal"}
      description="Add a new deal to your pipeline"
      maxWidth="3xl"
    >
      <div className="space-y-4">
        <DealForm
          onSubmit={handleSubmit}
          isLoading={isPending}
          initialValues={initialValues}
        />
      </div>
    </ResponsiveModal>
  );
}
